import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Mic, Video, Square, Circle, Upload, Library, Settings, LogOut, Sparkles } from "lucide-react";
import Footer from "../components/Footer";
import { useAuth } from "../contexts/AuthContext";
import UpgradeModal from "../components/UpgradeModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Studio() {
  const navigate = useNavigate();
  const { currentUser, logout, isPro } = useAuth();
  const [devices, setDevices] = useState({ cameras: [], microphones: [] });
  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMicrophone, setSelectedMicrophone] = useState("");
  const [stream, setStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [currentScript, setCurrentScript] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [newScriptTitle, setNewScriptTitle] = useState("");
  const [newScriptContent, setNewScriptContent] = useState("");
  const [scripts, setScripts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    loadDevices();
    loadScripts();
    loadUsage();
  }, []);

  const loadUsage = async () => {
    if (!currentUser) return;
    try {
      const response = await axios.get(`${API}/users/${currentUser.uid}/usage`);
      setUsage(response.data);
    } catch (error) {
      console.error('Error loading usage:', error);
    }
  };

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const loadDevices = async () => {
    try {
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      // Filter out devices with empty deviceId
      const cameras = deviceList.filter(device => device.kind === 'videoinput' && device.deviceId);
      const microphones = deviceList.filter(device => device.kind === 'audioinput' && device.deviceId);
      
      setDevices({ cameras, microphones });
      
      if (cameras.length > 0) setSelectedCamera(cameras[0].deviceId);
      if (microphones.length > 0) setSelectedMicrophone(microphones[0].deviceId);
    } catch (error) {
      console.error('Error loading devices:', error);
      toast.error('Failed to load devices');
    }
  };

  const loadScripts = async () => {
    if (!currentUser) return;
    try {
      const response = await axios.get(`${API}/scripts?user_id=${currentUser.uid}`);
      setScripts(response.data);
    } catch (error) {
      console.error('Error loading scripts:', error);
    }
  };

  const startPreview = async () => {
    try {
      // Try with selected devices first, fallback to any available device
      let constraints = {
        video: selectedCamera ? { deviceId: { ideal: selectedCamera } } : true,
        audio: selectedMicrophone ? { deviceId: { ideal: selectedMicrophone } } : true
      };
      
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        setStream(mediaStream);
        toast.success('Camera and microphone connected');
      } catch (err) {
        // If specific devices fail, try with any available devices
        console.warn('Failed with selected devices, trying defaults:', err);
        constraints = { video: true, audio: true };
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        setStream(mediaStream);
        toast.success('Connected with default devices');
        
        // Reload devices to update selections
        await loadDevices();
      }
    } catch (error) {
      console.error('Error starting preview:', error);
      const errorMsg = error.name === 'NotFoundError' 
        ? 'Camera/microphone not found. Please check device connections.'
        : error.name === 'NotAllowedError'
        ? 'Permission denied. Please allow camera/microphone access.'
        : 'Failed to access camera/microphone';
      toast.error(errorMsg);
    }
  };

  const stopPreview = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const startRecording = async () => {
    if (!stream) {
      toast.error('Please connect camera and microphone first');
      return;
    }

    try {
      const options = { mimeType: 'video/webm;codecs=vp9' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm';
      }

      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        
        // Create download link
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `recording-${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Save recording metadata
        try {
          const recording = await axios.post(`${API}/recordings`, {
            user_id: currentUser.uid,
            title: `Recording ${new Date().toLocaleDateString()}`,
            duration: recordingTime,
            script_id: currentScript?.id
          });
          
          // Store video blob URL for post-production page
          // Note: This URL will be revoked when navigating, so we store the blob as base64
          const reader = new FileReader();
          reader.onloadend = () => {
            sessionStorage.setItem(`recording_${recording.data.id}`, reader.result);
            
            // Update usage count
            loadUsage();
            
            toast.success('Recording saved!');
            
            // Navigate to post-production
            navigate(`/post-production/${recording.data.id}`);
          };
          reader.readAsDataURL(blob);
          
          // Clean up the download URL
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error saving recording:', error);
          
          // Check if it's a limit error
          if (error.response?.status === 403 && error.response?.data?.detail?.upgrade_required) {
            toast.error('Monthly recording limit reached! Upgrade to Pro for unlimited recordings.');
            setShowUpgradeModal(true);
          } else {
            toast.error('Recording downloaded but metadata not saved');
          }
        }
      };

      mediaRecorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const createScript = async () => {
    if (!newScriptTitle || !newScriptContent) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/scripts`, {
        user_id: currentUser.uid,
        title: newScriptTitle,
        content: newScriptContent
      });
      
      setScripts([...scripts, response.data]);
      setNewScriptTitle("");
      setNewScriptContent("");
      setScriptDialogOpen(false);
      toast.success('Script created');
    } catch (error) {
      console.error('Error creating script:', error);
      toast.error('Failed to create script');
    }
  };

  const loadScript = (script) => {
    setCurrentScript(script);
    setCurrentWordIndex(0);
    toast.success(`Script "${script.title}" loaded`);
  };

  const advanceWord = () => {
    if (currentScript) {
      const words = currentScript.content.split(/\s+/);
      if (currentWordIndex < words.length) {
        setCurrentWordIndex(prev => prev + 1);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const words = currentScript ? currentScript.content.split(/\s+/) : [];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Logo */}
            <div className="logo-placeholder"></div>
            <div className="border-l border-gray-200 pl-6">
              <h1 className="text-2xl font-bold font-manrope tracking-tight text-gray-900">Podmixr Labs</h1>
              <p className="text-sm text-gray-600 mt-1">Professional Podcast Studio</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">
              Hello, <strong>{currentUser?.displayName || currentUser?.email}</strong>
            </span>
            
            {/* Usage Indicator */}
            {usage && !usage.is_unlimited && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg border border-gray-200">
                <Circle className={`w-2 h-2 ${usage.recordings_remaining > 0 ? 'fill-green-500 text-green-500' : 'fill-red-500 text-red-500'}`} />
                <span className="text-xs font-medium text-gray-700">
                  {usage.recordings_used}/{usage.recordings_limit} recordings
                </span>
                {usage.recordings_remaining === 0 && (
                  <Button
                    size="sm"
                    onClick={() => setShowUpgradeModal(true)}
                    className="ml-2 bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 h-6"
                  >
                    <Sparkles className="w-3 h-3 mr-1" />
                    Upgrade
                  </Button>
                )}
              </div>
            )}
            
            {usage && usage.is_unlimited && (
              <div className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <span className="text-xs font-semibold text-blue-700">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  Pro - Unlimited
                </span>
              </div>
            )}
            
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              Home
            </Button>
            <Button
              data-testid="library-button"
              variant="ghost"
              onClick={() => navigate('/library')}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <Library className="w-4 h-4 mr-2" />
              Library
            </Button>
            <Button
              data-testid="logout-button"
              variant="ghost"
              onClick={async () => {
                await logout();
                toast.success('Logged out successfully');
                navigate('/login');
              }}
              className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Studio */}
      <div className="grid grid-cols-1 gap-4 p-6 bg-gray-50">
        {/* How to Test Video-Transcript Sync */}
        <Card className="bg-blue-50 border-blue-200 p-4">
          <div className="flex items-start gap-3">
            <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
              ℹ️
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">How to Test Video-Transcript Sync</h3>
              <div className="text-sm text-blue-800 space-y-2">
                <p><strong>Step 1:</strong> Create a script below with the text you want to speak</p>
                <p><strong>Step 2:</strong> Connect your camera and microphone, then click "Start Recording"</p>
                <p><strong>Step 3:</strong> Read the script while recording (use the teleprompter)</p>
                <p><strong>Step 4:</strong> After recording, go to Post-Production to see the interactive transcript sync with your video!</p>
                <p className="text-xs pt-2 border-t border-blue-200 mt-2">
                  💡 <strong>Sample Script:</strong> "Hello and welcome to the Podmixr Labs Podcast. Today we are discussing the future of content creation. Many creators struggle with video editing and post production work. Our AI tools can help you create professional content much faster than before."
                </p>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Camera Preview */}
        <div className="lg:col-span-2 space-y-4">
          <Card className={`relative bg-white border-gray-200 shadow-md overflow-hidden aspect-video ${
            isRecording ? 'recording-glow' : ''
          }`} data-testid="camera-preview-card">
            {stream ? (
              <video
                ref={videoRef}
                autoPlay
                muted
                className="w-full h-full object-cover"
                data-testid="camera-video"
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-900" data-testid="no-signal-screen">
                <div className="text-center">
                  <Video className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-400 font-mono text-sm">NO SIGNAL</p>
                </div>
              </div>
            )}
            
            {/* Recording indicator */}
            {isRecording && (
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 px-3 py-2 rounded shadow-lg" data-testid="recording-indicator">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse-slow" />
                <span className="font-mono text-sm text-white font-semibold">REC {formatTime(recordingTime)}</span>
              </div>
            )}

            {/* Device info */}
            {stream && (
              <div className="absolute bottom-4 left-4 bg-gray-900/90 px-3 py-1.5 rounded text-xs font-mono text-white" data-testid="device-info">
                {devices.cameras.find(c => c.deviceId === selectedCamera)?.label || 'Camera'}
              </div>
            )}
          </Card>

          {/* Controls */}
          <Card className="bg-white border-gray-200 shadow-md p-4">
            <div className="space-y-4">
              {/* Device Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm text-gray-700 font-medium flex items-center gap-2">
                    <Video className="w-4 h-4" />
                    Camera
                  </label>
                  <Select value={selectedCamera} onValueChange={setSelectedCamera} disabled={isRecording}>
                    <SelectTrigger className="bg-white border-gray-300 focus:border-blue-500" data-testid="camera-select">
                      <SelectValue placeholder="Select camera" />
                    </SelectTrigger>
                    <SelectContent>
                      {devices.cameras.length > 0 ? (
                        devices.cameras.map((camera) => (
                          camera.deviceId && (
                            <SelectItem key={camera.deviceId} value={camera.deviceId}>
                              {camera.label || `Camera ${camera.deviceId.slice(0, 8)}`}
                            </SelectItem>
                          )
                        ))
                      ) : (
                        <SelectItem value="no-camera" disabled>
                          No cameras found
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-gray-700 font-medium flex items-center gap-2">
                    <Mic className="w-4 h-4" />
                    Microphone
                  </label>
                  <Select value={selectedMicrophone} onValueChange={setSelectedMicrophone} disabled={isRecording}>
                    <SelectTrigger className="bg-white border-gray-300 focus:border-blue-500" data-testid="microphone-select">
                      <SelectValue placeholder="Select microphone" />
                    </SelectTrigger>
                    <SelectContent>
                      {devices.microphones.length > 0 ? (
                        devices.microphones.map((mic) => (
                          mic.deviceId && (
                            <SelectItem key={mic.deviceId} value={mic.deviceId}>
                              {mic.label || `Microphone ${mic.deviceId.slice(0, 8)}`}
                            </SelectItem>
                          )
                        ))
                      ) : (
                        <SelectItem value="no-microphone" disabled>
                          No microphones found
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                {!stream ? (
                  <Button
                    data-testid="connect-devices-button"
                    onClick={startPreview}
                    className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Connect Devices
                  </Button>
                ) : (
                  <>
                    {!isRecording ? (
                      <Button
                        data-testid="start-recording-button"
                        onClick={startRecording}
                        className="bg-red-600 hover:bg-red-700 text-white shadow-md"
                      >
                        <Circle className="w-4 h-4 mr-2" />
                        Start Recording
                      </Button>
                    ) : (
                      <Button
                        data-testid="stop-recording-button"
                        onClick={stopRecording}
                        className="bg-red-600 hover:bg-red-700 text-white shadow-md"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Stop Recording
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      onClick={stopPreview}
                      disabled={isRecording}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-900 border border-gray-300"
                      data-testid="disconnect-button"
                    >
                      Disconnect
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Right: Teleprompter */}
        <div className="space-y-4">
          <Card className="bg-white border-gray-200 shadow-md p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold font-manrope text-gray-900">Teleprompter</h2>
              <div className="flex items-center gap-2">
                <Dialog open={scriptDialogOpen} onOpenChange={setScriptDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="ghost" className="text-gray-600 hover:text-gray-900 hover:bg-gray-100" data-testid="upload-script-button">
                      <Upload className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-gray-200">
                    <DialogHeader>
                      <DialogTitle className="font-manrope text-gray-900">Create Script</DialogTitle>
                      <DialogDescription className="text-gray-600">
                        Create a new script for your podcast recording. The script will be displayed in the teleprompter.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="text-sm text-gray-700 font-medium mb-2 block">Title</label>
                        <Input
                          data-testid="script-title-input"
                          value={newScriptTitle}
                          onChange={(e) => setNewScriptTitle(e.target.value)}
                          className="bg-white border-gray-300 focus:border-blue-500"
                          placeholder="My Podcast Script"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-700 font-medium mb-2 block">Script Content</label>
                        <Textarea
                          data-testid="script-content-input"
                          value={newScriptContent}
                          onChange={(e) => setNewScriptContent(e.target.value)}
                          className="bg-white border-gray-300 focus:border-blue-500 h-48 font-mono"
                          placeholder="Enter your script here..."
                        />
                      </div>
                      <Button onClick={createScript} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="create-script-button">
                        Create Script
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Script Selector */}
            {scripts.length > 0 && (
              <div className="mb-4">
                <Select value={currentScript?.id || undefined} onValueChange={(value) => {
                  const script = scripts.find(s => s.id === value);
                  if (script) loadScript(script);
                }}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-blue-500" data-testid="script-selector">
                    <SelectValue placeholder="Select a script" />
                  </SelectTrigger>
                  <SelectContent>
                    {scripts.map((script) => (
                      script.id && (
                        <SelectItem key={script.id} value={script.id}>
                          {script.title}
                        </SelectItem>
                      )
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Script Display */}
            <div className="bg-gray-900 rounded-lg p-6 min-h-[400px] max-h-[500px] overflow-y-auto border border-gray-300" data-testid="script-display">
              {currentScript ? (
                <div className="font-mono text-3xl md:text-4xl leading-tight text-white teleprompter-text space-y-2">
                  {words.map((word, index) => (
                    <span
                      key={index}
                      className={`teleprompter-word ${index < currentWordIndex ? 'spoken' : ''}`}
                    >
                      {word}{' '}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <p className="text-sm">No script loaded. Create or select a script to begin.</p>
                </div>
              )}
            </div>

            {/* Script Controls */}
            {currentScript && (
              <div className="mt-4 flex items-center gap-2">
                <Button
                  data-testid="advance-word-button"
                  onClick={advanceWord}
                  size="sm"
                  className="bg-gray-200 hover:bg-gray-300 border border-gray-300 text-gray-900"
                >
                  Next Word
                </Button>
                <Button
                  data-testid="reset-script-button"
                  onClick={() => setCurrentWordIndex(0)}
                  size="sm"
                  variant="ghost"
                  className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  Reset
                </Button>
                <span className="text-xs text-gray-600 ml-auto font-mono">
                  {currentWordIndex} / {words.length} words
                </span>
              </div>
            )}
          </Card>
        </div>
        </div>
      </div>
      
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      
      <Footer />
    </div>
  );
}